/**
 * BaseModal.Alert - Alert Plugin for BaseModal
 * Simple alert dialogs with info/warning/error types
 *
 * @requires BasePlugin.js
 * @version 1.0.0
 */

'use strict';

import BasePlugin         from './BasePlugin.js';
import {generateModalId}  from '../constants.js';
import {waitForAnimation} from '../animations.js';
import AlertRenderer      from './alert/AlertRenderer.js';

/**
 * @typedef {Object} AlertConfig
 * @property {string} [selector='[data-basemodal-alert]'] - CSS selector
 * @property {string} [type='info'] - Alert type: 'info', 'warning', 'error'
 * @property {string} [theme='light'] - Modal theme
 * @property {string} [animation='slideUp'] - Animation type
 * @property {string} [size='small'] - Modal size
 * @property {number} [autoClose] - Auto-close delay in ms (0 = no auto-close)
 * @property {boolean} [showIcon=true] - Show alert icon
 * @property {string} [iconContent] - Custom icon HTML (text or SVG)
 * @property {Object} [icons] - Icons map by type
 * @property {boolean} [showTitle=true] - Show alert title
 * @property {string} [title] - Custom alert title
 * @property {boolean} [showCloseButton=true] - Show close button
 * @property {string} [closeButtonContent='&times;'] - Close button content (text or SVG)
 * @property {boolean} [showOverlay=true] - Show overlay background
 * @property {boolean} [debugMode=false] - Debug mode
 * @property {AlertLocales} [locales] - Localization
 * @property {boolean} [showConfirmButton=false] - Show confirm action button
 * @property {boolean} [showCancelButton=false] - Show cancel action button
 * @property {string} [confirmButtonContent='OK'] - Confirm button content (text or SVG)
 * @property {string} [cancelButtonContent='Cancel'] - Cancel button content (text or SVG)
 * @property {Function} [onConfirm] - Confirm callback
 * @property {Function} [onCancel] - Cancel callback
 */

/**
 * @typedef {Object} AlertLocales
 * @property {string} [close='Close'] - Close button text
 * @property {string} [info='Info'] - Info title
 * @property {string} [warning='Warning'] - Warning title
 * @property {string} [error='Error'] - Error title
 * @property {string} [confirm='OK'] - Confirm text
 * @property {string} [cancel='Cancel'] - Cancel text
 */

// ========================================
// CONSTANTS & DEFAULTS
// ========================================

/**
 * Default configuration for alert plugin
 * @type {AlertConfig}
 */
const DEFAULTS = {
    selector            :'[data-basemodal-alert]',
    closeAttribute      :'data-basemodal-close',
    type                :'info',
    theme               :'light',
    animation           :'slideUp',
    size                :'small',
    autoClose           :0,
    showIcon            :true,
    iconContent         :'',
    icons               :null,
    showTitle           :true,
    title               :'',
    showCloseButton     :true,
    closeButtonContent  :'&times;',
    showOverlay         :true,
    debugMode           :false,
    locales             :{
        close  :'Close',
        info   :'Info',
        warning:'Warning',
        error  :'Error',
        confirm:'OK',
        cancel :'Cancel'
    },
    showConfirmButton   :false,
    showCancelButton    :false,
    confirmButtonContent:'OK',
    cancelButtonContent :'Cancel',
    onConfirm           :null,
    onCancel            :null
};

// ========================================
// MAIN CLASS
// ========================================

/**
 * BaseModal.Alert Class
 */
export default class BaseModalAlert extends BasePlugin {
    /**
     * Create an alert plugin instance
     * @param {AlertConfig} config - Configuration options
     */
    constructor(config = {}) {
        super(config);
        this.config       = {...DEFAULTS, ...config};
        this.renderer     = new AlertRenderer(this.config);
        this.activeModals = new Map();
        this.queue        = [];
        this.isShowing    = false;
    }

    /**
     * Set initial focus inside alert modal
     * Priority: confirm -> cancel -> close -> first focusable
     * @param {HTMLElement} modal - Modal element
     * @private
     */
    focusAlert(modal) {
        const focusables = modal.querySelectorAll(
            '[data-alert-action], .bm-modal__close, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

        const pick = () => {
            const confirmBtn = modal.querySelector('[data-alert-action="confirm"]');
            if (confirmBtn) {
                confirmBtn.focus();
                return;
            }

            const cancelBtn = modal.querySelector('[data-alert-action="cancel"]');
            if (cancelBtn) {
                cancelBtn.focus();
                return;
            }

            const closeBtn = modal.querySelector('.bm-modal__close');
            if (closeBtn) {
                closeBtn.focus();
                return;
            }

            const firstFocusable = focusables.length > 0 ? focusables[0] : null;
            if (firstFocusable && typeof firstFocusable.focus === 'function') {
                firstFocusable.focus();
            }
        };

        requestAnimationFrame(pick);
    }

    // ========================================
    // INITIALIZATION
    // ========================================

    /**
     * Initialize alert plugin
     * Attaches event listeners to document for delegation
     * @public
     */
    init(baseModal) {
        if (!baseModal) {
            console.error('BaseModal.Alert: BaseModal instance is required');
            return;
        }

        super.init(baseModal);
        this.attachEventListeners();

        if (this.config.debugMode) {
            console.log('BaseModal.Alert: Event listeners attached');
        }
    }

    /**
     * Attach click event listeners using delegation
     * @private
     */
    attachEventListeners() {
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest(this.config.selector);
            if (trigger) {
                e.preventDefault();
                const message = trigger.dataset.message || trigger.textContent;
                const type    = trigger.dataset.type || this.config.type;
                void this.show(message, type);
            }
        });
    }

    // ========================================
    // ALERT DISPLAY
    // ========================================

    /**
     * Show alert message
     * @param {string | {message: string, type?: string, alertType?: string}} messageOrOptions - Alert message или объект с параметрами
     * @param {string} [type] - Alert type: 'info', 'warning', 'error'
     * @param {Object} [options] - Additional options
     * @returns {Promise<string>} Modal ID
     * @public
     */
    async show(messageOrOptions, type = this.config.type, options = {}) {
        const isObjectCall      = typeof messageOrOptions === 'object' && messageOrOptions !== null && !Array.isArray(messageOrOptions);
        const normalizedMessage = isObjectCall ? messageOrOptions.message : messageOrOptions;
        const normalizedType    = isObjectCall ? (messageOrOptions.type || messageOrOptions.alertType || this.config.type) : type;
        const normalizedOptions = isObjectCall ? {...messageOrOptions} : options;

        const payload = {
            message:normalizedMessage,
            type   :normalizedType,
            options:normalizedOptions
        };

        return new Promise((resolve, reject) => {
            this.queue.push({
                payload,
                resolve,
                reject
            });
            void this.processQueue();
        });
    }

    async processQueue() {
        if (this.isShowing) {
            return;
        }

        const next = this.queue.shift();
        if (!next) {
            return;
        }

        this.isShowing = true;

        try {
            const modalId = await this.showSingle(next.payload);
            next.resolve(modalId);
        } catch (error) {
            next.reject(error);
        }
    }

    async showSingle({
        message,
        type,
        options
    }) {
        const mergedConfig = {
            ...this.config,
            ...options,
            type
        };

        const modalId   = generateModalId('alert');
        const modalHTML = this.renderer.generateAlertHTML(modalId, message, mergedConfig);

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.getElementById(modalId);

        const actionHandler = async (e) => {
            const actionBtn = e.target.closest('[data-alert-action]');
            if (!actionBtn) {
                return;
            }

            e.preventDefault();
            const actionType = actionBtn.dataset.alertAction;

            if (actionType === 'confirm') {
                const shouldClose = mergedConfig.onConfirm ? mergedConfig.onConfirm(modalId) !== false : true;
                if (shouldClose) {
                    await this.close(modalId);
                }
                return;
            }

            if (actionType === 'cancel') {
                const shouldClose = mergedConfig.onCancel ? mergedConfig.onCancel(modalId) !== false : true;
                if (shouldClose) {
                    await this.close(modalId);
                }
            }
        };

        if (modal) {
            modal.addEventListener('click', actionHandler);
            modal._actionHandler = actionHandler;
            this.focusAlert(modal);
        }

        try {
            await this.showModal(modalId, {
                theme              :mergedConfig.theme,
                animation          :mergedConfig.animation,
                size               :mergedConfig.size,
                awaitCloseAnimation:true,
                onClose            :() => {
                    this.cleanup(modalId);
                }
            });

            this.activeModals.set(modalId, message);

            if (mergedConfig.autoClose > 0) {
                setTimeout(() => {
                    this.close(modalId);
                }, mergedConfig.autoClose);
            }

            return modalId;
        } catch (error) {
            console.error('BaseModal.Alert: Error showing alert:', error);
            this.cleanup(modalId);
            throw error;
        }
    }

    /**
     * Close alert by modal ID
     * @param {string} modalId - Modal ID to close
     * @public
     */
    async close(modalId) {
        const modal = document.getElementById(modalId);
        if (!modal) {
            return;
        }

        try {
            await this.closeModal(modalId);
        } catch (error) {
            console.error('BaseModal.Alert: Error closing alert:', error);
        }
    }

    /**
     * Close all alerts
     * @public
     */
    async closeAll() {
        const promises = [];
        for (const modalId of this.activeModals.keys()) {
            promises.push(this.close(modalId));
        }
        await Promise.all(promises);
    }

    // ========================================
    // CLEANUP
    // ========================================

    /**
     * Cleanup alert modal
     * @param {string} modalId - Modal ID
     * @private
     */
    cleanup(modalId) {
        const modal = document.getElementById(modalId);

        if (!modal) {
            this.activeModals.delete(modalId);
            this.isShowing = false;
            void this.processQueue();
            return;
        }

        waitForAnimation(this, modal, () => {
            if (modal._actionHandler) {
                modal.removeEventListener('click', modal._actionHandler);
            }

            modal.remove();
            this.activeModals.delete(modalId);
            this.isShowing = false;
            void this.processQueue();
        });
    }

    // ========================================
    // STATIC METHODS
    // ========================================

    /**
     * Show info alert
     * @param {string} message - Message to display
     * @returns {Promise<string>} Modal ID
     * @public
     */
    async info(message) {
        return this.show(message, 'info');
    }

    /**
     * Show warning alert
     * @param {string} message - Message to display
     * @returns {Promise<string>} Modal ID
     * @public
     */
    async warning(message) {
        return this.show(message, 'warning');
    }

    /**
     * Show error alert
     * @param {string} message - Message to display
     * @returns {Promise<string>} Modal ID
     * @public
     */
    async error(message) {
        return this.show(message, 'error');
    }

    /**
     * Destroy alert plugin instance
     * Cleans up all active alerts
     * @public
     */
    destroy() {
        this.activeModals.forEach((data, modalId) => {
            const modal = document.getElementById(modalId);
            if (modal) {
                modal.remove();
            }
        });
        this.activeModals.clear();
        super.destroy();
    }
}
